import React, { useState, useEffect, useRef } from 'react';
import { ToggleButtonProps } from './types';

interface PersonaToggleButtonProps extends ToggleButtonProps {
  accentColor?: string;
  accentColorHex?: string;
  iconLabel?: string;
  toggleGlow?: string;
}

// 4-pointed sparkle — the universal AI icon
const SparkleIcon: React.FC<{ size?: number }> = ({ size = 22 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    {/* Large 4-pointed star */}
    <path
      d="M12 2.5 C12 2.5 13 8.5 13.8 10.2 C14.6 11.9 16 12 16 12 C16 12 14.6 12.1 13.8 13.8 C13 15.5 12 21.5 12 21.5 C12 21.5 11 15.5 10.2 13.8 C9.4 12.1 8 12 8 12 C8 12 9.4 11.9 10.2 10.2 C11 8.5 12 2.5 12 2.5 Z"
      fill="white"
    />
    {/* Small sparkle top-right */}
    <path
      d="M19 3 C19 3 19.55 5.45 19.9 6.1 C20.25 6.75 21 6.9 21 6.9 C21 6.9 20.25 7.05 19.9 7.7 C19.55 8.35 19 11 19 11 C19 11 18.45 8.35 18.1 7.7 C17.75 7.05 17 6.9 17 6.9 C17 6.9 17.75 6.75 18.1 6.1 C18.45 5.45 19 3 19 3 Z"
      fill="white"
      opacity={0.75}
    />
    {/* Tiny dot bottom-left */}
    <circle cx="5.5" cy="17.5" r="1.5" fill="white" opacity={0.5} />
  </svg>
);

// Rotating prompts shown in the intermittent bubble
const PROMPTS = [
  'Need help?',
  'Ask me anything',
  'AI Assistant',
  'How can I help?',
];

const ToggleButton: React.FC<PersonaToggleButtonProps> = ({
  isOpen,
  onToggle,
  className = '',
  hasUnreadMessages = false,
  accentColorHex = '#0f172a',
}) => {
  const [hovered, setHovered] = useState(false);
  const [nudgeVisible, setNudgeVisible] = useState(false);
  const [promptIndex, setPromptIndex] = useState(0);
  const promptIndexRef = useRef(0);
  const showAlert = hasUnreadMessages && !isOpen;

  // Intermittent chatbot-style nudge: show for 4s every 12s
  useEffect(() => {
    if (isOpen) {
      setNudgeVisible(false);
      return;
    }

    let hideTimer: ReturnType<typeof setTimeout>;
    let interval: ReturnType<typeof setInterval>;

    const show = () => {
      setNudgeVisible(true);
      clearTimeout(hideTimer);
      hideTimer = setTimeout(() => setNudgeVisible(false), 7000);
    };

    // First nudge after 5s
    const initialDelay = setTimeout(() => {
      show();
      // Then repeat every 25s, rotating prompt
      interval = setInterval(() => {
        promptIndexRef.current = (promptIndexRef.current + 1) % PROMPTS.length;
        setPromptIndex(promptIndexRef.current);
        show();
      }, 25000);
    }, 5000);

    return () => {
      clearTimeout(initialDelay);
      clearTimeout(hideTimer);
      clearInterval(interval);
    };
  }, [isOpen]);

  const labelVisible = (hovered || nudgeVisible) && !isOpen;

  return (
    <div className={`fixed bottom-8 right-8 z-50 flex items-center gap-3 ${className}`}>

      {/* ── Chatbot nudge bubble, slides in from right ── */}
      <div
        className="pointer-events-none select-none"
        style={{
          opacity: labelVisible ? 1 : 0,
          transform: labelVisible ? 'translateX(0) scale(1)' : 'translateX(10px) scale(0.95)',
          transition: 'opacity 0.25s ease, transform 0.25s cubic-bezier(.34,1.56,.64,1)',
        }}
      >
        {/* Chat bubble shape with tail */}
        <div className="relative">
          <span
            className="block text-[11px] font-semibold text-white whitespace-nowrap px-3 py-1.5 rounded-xl shadow-lg"
            style={{ background: `${accentColorHex}f0`, backdropFilter: 'blur(8px)' }}
          >
            {PROMPTS[promptIndex]}
          </span>
          {/* Bubble tail pointing right toward the button */}
          <span
            className="absolute top-1/2 -right-1.5 -translate-y-1/2 w-0 h-0"
            style={{
              borderTop: '5px solid transparent',
              borderBottom: '5px solid transparent',
              borderLeft: `6px solid ${accentColorHex}f0`,
            }}
          />
        </div>
      </div>

      {/* ── Button stack ── */}
      <div
        className="relative flex items-center justify-center"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {/* Breathing outer glow — only when closed */}
        {!isOpen && (
          <span
            className="ai-orb-breathe absolute rounded-full pointer-events-none"
            style={{
              width: 72,
              height: 72,
              background: accentColorHex,
            }}
          />
        )}

        {/* Spinning arc ring — only when closed */}
        {!isOpen && (
          <span
            className="ai-arc-spin absolute rounded-full pointer-events-none"
            style={{
              width: 62,
              height: 62,
              background: `conic-gradient(from 0deg, transparent 0%, ${accentColorHex}cc 35%, transparent 60%)`,
              padding: 2,
            }}
          />
        )}

        {/* Inner static ring */}
        {!isOpen && (
          <span
            className="absolute rounded-full pointer-events-none"
            style={{
              width: 56,
              height: 56,
              border: `1.5px solid ${accentColorHex}40`,
            }}
          />
        )}

        {/* Main button */}
        <button
          onClick={onToggle}
          aria-label={isOpen ? 'Close AI Assistant' : 'Open AI Assistant'}
          aria-expanded={isOpen}
          style={{
            width: 52,
            height: 52,
            background: isOpen
              ? `linear-gradient(145deg, ${accentColorHex}bb, ${accentColorHex})`
              : `linear-gradient(145deg, ${accentColorHex}f0, ${accentColorHex})`,
            boxShadow: isOpen
              ? `0 4px 16px ${accentColorHex}50`
              : `0 8px 28px ${accentColorHex}70, 0 2px 8px ${accentColorHex}40`,
            transform: isOpen
              ? 'scale(0.9)'
              : hovered
              ? 'scale(1.08)'
              : nudgeVisible
              ? 'scale(1.04)'
              : 'scale(1)',
            transition: 'transform 0.25s cubic-bezier(.34,1.56,.64,1), box-shadow 0.25s ease, border-radius 0.3s ease',
            borderRadius: isOpen ? 14 : 18,
          }}
          className="relative flex items-center justify-center focus:outline-none"
        >
          {/* Notification dot */}
          {showAlert && (
            <span className="absolute -top-1 -right-1 flex h-3.5 w-3.5">
              <span
                className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-60"
                style={{ backgroundColor: '#f87171' }}
              />
              <span className="relative inline-flex h-3.5 w-3.5 rounded-full bg-red-500 border-[2px] border-white" />
            </span>
          )}

          {/* Icon */}
          <span
            style={{
              transition: 'transform 0.3s cubic-bezier(.34,1.56,.64,1), opacity 0.2s ease',
              transform: isOpen ? 'rotate(90deg) scale(0.9)' : 'rotate(0deg) scale(1)',
            }}
          >
            {isOpen ? (
              // Close X — thinner, minimal
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M6 18L18 6M6 6l12 12"
                  stroke="white"
                  strokeWidth={2.5}
                  strokeLinecap="round"
                />
              </svg>
            ) : (
              <SparkleIcon size={22} />
            )}
          </span>
        </button>
      </div>
    </div>
  );
};

export default React.memo(ToggleButton);
